import { forwardRef, useMemo } from "react";

import clsx from "clsx";
import { get } from "lodash";
import moment, { type Moment } from "moment";
import { SingleDatePicker } from "react-dates";
import { useEffectOnce, useEventListener, useToggle } from "usehooks-ts";

const DISPLAY_FORMAT = "MMM D, YYYY";

interface DatePickerProps {
  name?: string;
  label?: string;
  className?: string;
  isDisabled?: boolean;
  value?: string | null;
  onChange: (value: string | null) => void;
  timezone: string;
  placeholder?: string;
  isOutsideRange?: (date: Moment) => boolean;
  errorMessage?: string;
  errors?: any;
  appendToBody?: boolean;
}

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      name,
      label,
      value,
      className,
      onChange,
      isDisabled = false,
      timezone,
      placeholder = "Select Date",
      isOutsideRange,
      errorMessage,
      errors,
      appendToBody = false,
      ...props
    },
    ref,
  ) => {
    const [isOpen, toggleOpen, setIsOpen] = useToggle();
    const close = () => setIsOpen(false);
    useEffectOnce(() => {
      moment.tz.setDefault(timezone);
    });

    const onDateSelect = (newDate: string) => {
      close();
      onChange(newDate);
    };

    useEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
        event.stopPropagation();
      }
    });

    const displayText = value
      ? moment(value).format(DISPLAY_FORMAT)
      : placeholder;

    // Access the error message from react-hook-form
    const formErrorMessage = get(errors, `${name}.message`);
    const hasError = errorMessage || formErrorMessage;

    return (
      <div
        className={clsx("nb-input-container nb-date-picker", className)}
        {...props}
      >
        {label && <label className="label">{label}</label>}
        <button
          className="nb-input flex items-center justify-between"
          onClick={toggleOpen}
          disabled={isDisabled}
          type="button"
        >
          <div>{displayText}</div>
          <span className="text-cosmic">
            <i className="fa-regular fa-sharp fa-calendars" />
          </span>
        </button>
        {isOpen && (
          <span className="calendar z-[3001]">
            <Calendar
              date={value}
              onChange={onDateSelect}
              isOpen={isOpen}
              isOutsideRange={isOutsideRange}
              close={close}
              appendToBody={appendToBody}
            />
          </span>
        )}
        {hasError && (
          <div className="error-message">
            {errorMessage ?? formErrorMessage}
          </div>
        )}
      </div>
    );
  },
);

interface CalendarProps {
  date?: string | null;
  onChange(date: string | null): void;
  isOutsideRange?: (date: Moment) => boolean;
  isOpen: boolean;
  close: () => void;
  appendToBody: boolean;
}

const lastMonth = () => moment().startOf("month").subtract(1, "month");

const Calendar = ({
  date,
  onChange,
  isOpen,
  isOutsideRange = () => false,
  close,
  appendToBody,
}: CalendarProps) => {
  const dateMoment = useMemo(() => (date ? moment(date) : null), [date]);

  const selectDate = (date: Moment | null) => {
    if (date) {
      onChange(date.format("YYYY-MM-DD"));
    }
  };

  return (
    <SingleDatePicker
      id="single-date-picker"
      placeholder=""
      date={dateMoment}
      onDateChange={selectDate}
      focused={isOpen}
      noBorder
      initialVisibleMonth={lastMonth}
      onFocusChange={close}
      displayFormat={DISPLAY_FORMAT}
      numberOfMonths={2}
      isOutsideRange={isOutsideRange}
      small
      block
      hideKeyboardShortcutsPanel
      appendToBody={appendToBody}
    />
  );
};
